import React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Column, Table, TableBody, TableHead, TableHeadCell, tableHooks, TablePagination } from '@trackman/web-shared-components';
import { CenteredLoader } from '@trackman/baseball-shared-components';
import { ReturnedRow } from 'constants/types/TableTypes';
import { ISession } from 'interfaces/session/ISession';
import { FiltersData } from './TableFilters';
import {
  StyledRow,
  StyledTableBodyRow,
  StyledTableBodyCell,
  StyledNoData,
  StyledTableHeadRow,
  StyledFilters,
} from './styles/SimpleTableStyles';
import { ReactComponent as BackIcon } from '../icons/backIcon.svg';
import { History } from 'history';

const ROWS_PER_PAGE = 10;

interface IProps {
  sessions: ISession[];
  columns: Column[];
  data: any[];
  page: number;
  onChangePage: (page: number) => void;
  filters: FiltersData;
  onHeaderClick?: (columnKey: string, defaultClick?: () => void) => void;
  sort: any;
  isLoading?: boolean;
  history?: History;
}

const SimpleTable: React.FC<IProps> = observer((props) => {
  const { sessions, columns, data, page, onChangePage, filters, onHeaderClick, sort, isLoading } = props;
  const { useTable, useSortBy, usePagination, useFlexLayout, useRowSelect } = tableHooks;
  const { getTableProps, rows, headerGroups, prepareRow } = useTable(
    {
      columns,
      data,
    },
    useSortBy,
    usePagination,
    useFlexLayout,
    useRowSelect
  );

  const isDisabled = (row: ReturnedRow): boolean => {
    return false;
  };

  return (
    <>
      <StyledFilters>{filters?.current}</StyledFilters>
      <Table {...getTableProps()}>
        <TableHead dark>
          {headerGroups.map(({ headers, getHeaderGroupProps }, index) => (
            <StyledTableHeadRow key={index} {...getHeaderGroupProps()}>
              {headers.map(({ sortable, align, render, getHeaderProps, getSortByToggleProps }, index) => {
                const columnKey = getHeaderProps().key.split('_')[1];

                return onHeaderClick && sortable ? (
                  <TableHeadCell
                    key={index}
                    {...getHeaderProps(sortable && getSortByToggleProps())}
                    sortable={sortable}
                    sortDirection={columnKey === sort.column && sort.direction}
                    align={align}
                    onClick={(e: React.MouseEventHandler<HTMLTableCellElement>): void => {
                      if (sortable) {
                        onHeaderClick(columnKey, () => getSortByToggleProps().onClick(e));
                      }
                    }}
                  >
                    {render('Header')}
                    {filters?.contentIcons[index]}
                  </TableHeadCell>
                ) : (
                  <TableHeadCell key={index} {...getHeaderProps()} align={align}>
                    {render('Header')}
                    {filters?.contentIcons[index]}
                  </TableHeadCell>
                );
              })}
            </StyledTableHeadRow>
          ))}
        </TableHead>
        <TableBody>
          {isLoading ? (
            <CenteredLoader size='large' />
          ) : (
            <>
              {rows.length > 0 ? (
                rows.map((row, index) => {
                  prepareRow(row);
                  return (
                    <StyledRow key={index} disabled={isDisabled(row)}>
                      <StyledTableBodyRow index={index} {...row.getRowProps()}>
                        {row.cells.map(({ getCellProps, column, render }) => (
                          <StyledTableBodyCell {...getCellProps()} align={column.align}>
                            {render('Cell')}
                          </StyledTableBodyCell>
                        ))}
                      </StyledTableBodyRow>
                    </StyledRow>
                  );
                })
              ) : (
                <StyledNoData>
                  <div>No sessions found</div>
                  <Button
                    intent='primary'
                    label='Back to search'
                    onClick={() => {
                      props.history?.push(`/`);
                    }}
                    startIcon={<BackIcon />}
                  />
                </StyledNoData>
              )}
            </>
          )}
        </TableBody>
      </Table>
      <TablePagination page={page} rowsPerPage={ROWS_PER_PAGE} count={sessions.length} onChangePage={onChangePage} />
    </>
  );
});

export default SimpleTable;
